import type { EChartsOption } from 'echarts';
import type { SandboxDashboardTrendPoint } from '@angular-workspace/services';
import type { SandboxDashboardTheme } from './sandbox-dashboard.theme';
import { formatIstDayLabel, formatSandboxHours, isFiniteNumber } from './sandbox-dashboard.util';

export type SandboxTrendChartMode = 'grouped' | 'area';

export const SANDBOX_TREND_CHART_MODES: { value: SandboxTrendChartMode; label: string }[] = [
  { value: 'grouped', label: 'Bar chart' },
  { value: 'area', label: 'Area chart' },
];

export interface SandboxTrendChartContext {
  points: SandboxDashboardTrendPoint[];
  theme: SandboxDashboardTheme;
  showCpu: boolean;
  showGpu: boolean;
  reduceMotion: boolean;
}

interface Series {
  name: 'CPU' | 'GPU';
  color: string;
  values: number[];
}

type AxisTooltipParam = { marker?: string; seriesName?: string; value?: unknown; name?: string };

const round2 = (v: number | null | undefined) => (isFiniteNumber(v) ? Math.round(v * 100) / 100 : 0);

/** Hex → rgba for canvas gradients (CSS color-mix is not understood by the chart renderer). */
function withAlpha(color: string, alpha: number): string {
  const hex = color.trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function selectedSeries(ctx: SandboxTrendChartContext): Series[] {
  const series: Series[] = [];
  if (ctx.showCpu) {
    series.push({ name: 'CPU', color: ctx.theme.chartCpuColor, values: ctx.points.map((p) => round2(p.bookedSessionHours?.cpu)) });
  }
  if (ctx.showGpu) {
    series.push({ name: 'GPU', color: ctx.theme.chartGpuColor, values: ctx.points.map((p) => round2(p.bookedSessionHours?.gpu)) });
  }
  return series;
}

function axisTooltip(raw: unknown): string {
  const params = (Array.isArray(raw) ? raw : [raw]) as AxisTooltipParam[];
  const lines = params.map(
    (p) => `${p.marker ?? ''}${p.seriesName}&nbsp;&nbsp;<b>${formatSandboxHours(Number(p.value))}</b>`,
  );
  return [params[0]?.name ?? '', ...lines].join('<br/>');
}

function cartesianBase(ctx: SandboxTrendChartContext): EChartsOption {
  const { theme } = ctx;
  return {
    animation: !ctx.reduceMotion,
    animationDuration: 700,
    animationEasing: 'cubicOut',
    animationDurationUpdate: 400,
    textStyle: { fontFamily: theme.fontFamily },
    legend: { show: false },
    grid: { left: 48, right: 12, top: 16, bottom: 28 },
    xAxis: {
      type: 'category',
      data: ctx.points.map((p) => formatIstDayLabel(p.start)),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: theme.border } },
      axisLabel: { color: theme.textSecondary },
    },
    yAxis: {
      type: 'value',
      name: 'Hours',
      nameLocation: 'middle',
      nameGap: 34,
      nameTextStyle: { color: theme.textSecondary },
      axisLabel: { color: theme.textSecondary },
      splitLine: { lineStyle: { color: theme.chartGridLine } },
    },
  };
}

function barChart(ctx: SandboxTrendChartContext): EChartsOption {
  return {
    ...cartesianBase(ctx),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: axisTooltip },
    series: selectedSeries(ctx).map((s) => ({
      name: s.name,
      type: 'bar' as const,
      barMaxWidth: 40,
      data: s.values,
      itemStyle: { color: s.color, borderRadius: [4, 4, 0, 0] },
      emphasis: { focus: 'series' as const },
      // Bars grow in one after another.
      animationDelay: (index: number) => index * 40,
    })),
  };
}

function areaChart(ctx: SandboxTrendChartContext): EChartsOption {
  return {
    ...cartesianBase(ctx),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: ctx.theme.border } },
      formatter: axisTooltip,
    },
    series: selectedSeries(ctx).map((s) => ({
      name: s.name,
      type: 'line' as const,
      data: s.values,
      // Monotone smoothing avoids dipping below zero between sparse days.
      smooth: 0.35,
      smoothMonotone: 'x' as const,
      symbol: 'circle',
      symbolSize: 6,
      showSymbol: true,
      lineStyle: { color: s.color, width: 2.5 },
      itemStyle: { color: s.color },
      areaStyle: {
        color: {
          type: 'linear' as const,
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: withAlpha(s.color, 0.35) },
            { offset: 1, color: withAlpha(s.color, 0) },
          ],
        },
      },
      emphasis: { focus: 'series' as const },
    })),
  };
}

/** Builds the ECharts option for the daily-hours panel in the chosen view. */
export function buildSandboxTrendChart(mode: SandboxTrendChartMode, ctx: SandboxTrendChartContext): EChartsOption | null {
  if (ctx.points.length === 0 || (!ctx.showCpu && !ctx.showGpu)) return null;
  return mode === 'area' ? areaChart(ctx) : barChart(ctx);
}
